/** @format */

import React, { useState } from 'react'
import { _$, Table, TableColumn, Pagination, dayjs, TableSortValue } from '@king-design/react'
import styled from 'styled-components'
import { ServiceOverviewSearchProps } from './../../components/typescript.js'
import {
  SessionAnalysisListItem,
  SessionAnalysisSearchProps,
  isSessionMatchFilter,
  isSessionRangeFilter
} from './../typescript'
import { TruncatedValueWithTooltip } from 'src/components'
import { useHistory, useLocation } from 'react-router'
import { useAsync } from 'react-use'
import { getSessionList } from 'src/api/ApiAIObservability'
import { durationFormatCompact } from 'src/utils/common'
import { tokenValueFormat } from '../../constant'
import variable from 'src/static/styles/var'
import useWindowSize from 'src/hooks/useWindowSize'

interface SessionTableProps {
  className?: string
  serviceName?: string
  headerSearch: ServiceOverviewSearchProps
  sessionSearch: SessionAnalysisSearchProps
  setSessionSearch: (search: SessionAnalysisSearchProps) => void
  region?: string
}

function useQuery() {
  return new URLSearchParams(useLocation().search)
}
const SessionAnalysis: React.FC<SessionTableProps> = ({
  className,
  headerSearch,
  serviceName: embedServiceName,
  sessionSearch,
  setSessionSearch,
  region
}) => {
  const query = useQuery()
  const runtime = query.get('runtime')
  const [tableSort, setTableSort] = useState<TableSortValue>()
  const { containerHeight: tableHeight } = useWindowSize({ offset: embedServiceName ? 137 : 88 })
  const history = useHistory()
  const location = useLocation()
  const { value: sessionData, loading } = useAsync(async () => {
    if (headerSearch?.time[0] && headerSearch?.time[1]) {
      // 仅提交有值的筛选项：文本匹配需 Values 非空，区间需 Start/End 至少填一项
      const result = await getSessionList({
        Page: sessionSearch.Page,
        PageSize: sessionSearch.PageSize,
        Order: tableSort?.type,
        OrderBy: tableSort?.key,
        StartTime: headerSearch?.time[0],
        EndTime: headerSearch?.time[1],
        Filters: sessionSearch.Filters.filter(item => {
          if (isSessionMatchFilter(item)) {
            return item.Values.length > 0
          }
          if (isSessionRangeFilter(item)) {
            return item.Start && item.End
          }
          return false
        })
      })
      return {
        data: result?.Sessions || [],
        total: result?.TotalCount || 0
      }
    } else {
      return {}
    }
  }, [headerSearch?.time, sessionSearch, tableSort, region])

  // 调钻会话详情
  const openSessionDetailTab = (row: SessionAnalysisListItem) => {
    const qs = new URLSearchParams({
      serviceName: row.ServiceName,
      sessionId: row.SessionId
    })
    let hashPath = ''
    if (location?.pathname.includes('/monitor/agentkit')) {
      hashPath = `/monitor/agentkit/runtimedetail/sessionanDetail?${qs.toString()}&runtime=${runtime}`
    } else if (embedServiceName) {
      hashPath = `/monitor/aiobservability/servicedetail/sessionanDetail?${qs.toString()}`
    } else {
      hashPath = `/monitor/aiobservability/sessionanalysis/detail?${qs.toString()}`
    }
    history.push(hashPath)
  }

  return (
    <div className={className}>
      <Table
        data={sessionData?.data || []}
        rowKey={data => `${data.ServiceName}-${data.SessionId}-${data.LastActiveTime}`}
        checkType="none"
        loading={loading}
        fixHeader={tableHeight}
        animation={false}
        sort={tableSort}
        resizable={true}
        onChangeSort={sort => {
          setTableSort(sort)
        }}
      >
        <TableColumn
          key="SessionId"
          width={200}
          title={_$('会话ID')}
          fixed="left"
          slotTemplate={([data]) => {
            return (
              <TruncatedValueWithTooltip value={data.SessionId} copyable>
                <a
                  onClick={e => {
                    e.stopPropagation()
                    openSessionDetailTab(data)
                  }}
                >
                  {data.SessionId}
                </a>
              </TruncatedValueWithTooltip>
            )
          }}
        />
        <TableColumn
          key="ServiceName"
          title={_$('所属服务')}
          width={120}
          slotTemplate={([data]) => {
            return <TruncatedValueWithTooltip>{data.ServiceName}</TruncatedValueWithTooltip>
          }}
        />
        <TableColumn
          key="DurationMs"
          title={_$('耗时')}
          width={120}
          sortable
          slotTemplate={([data]) => {
            return data.DurationMs != null ? durationFormatCompact(data.DurationMs) : ''
          }}
        />
        <TableColumn
          key="TotalTokens"
          title={_$('Total Tokens')}
          width={120}
          sortable
          slotTemplate={([data]) => {
            return tokenValueFormat(data.TotalTokens)
          }}
        />
        <TableColumn
          key="InputTokens"
          title={_$('输入Token')}
          width={120}
          sortable
          slotTemplate={([data]) => {
            return tokenValueFormat(data.InputTokens)
          }}
        />
        <TableColumn
          key="OutputTokens"
          title={_$('输出Token')}
          width={120}
          sortable
          slotTemplate={([data]) => {
            return tokenValueFormat(data.OutputTokens)
          }}
        />
        <TableColumn
          key="TraceCount"
          title={_$('调用链数量')}
          width={120}
          sortable
          slotTemplate={([data]) => {
            return tokenValueFormat(data.TraceCount)
          }}
        />
        <TableColumn
          key="StartTime"
          title={_$('会话开始时间')}
          width={120}
          slotTemplate={([data]) => {
            return <div>{dayjs(data.StartTime).format('YYYY-MM-DD HH:mm:ss')}</div>
          }}
        />
        <TableColumn
          key="EndTime"
          title={_$('会话结束时间')}
          width={120}
          slotTemplate={([data]) => {
            return <div>{dayjs(data.EndTime).format('YYYY-MM-DD HH:mm:ss')}</div>
          }}
        />
      </Table>
      {sessionData !== undefined ? (
        <Pagination
          value={sessionSearch.Page}
          limit={sessionSearch.PageSize}
          total={sessionData?.total || 0}
          size="small"
          onChangeValue={value => {
            setSessionSearch({
              ...sessionSearch,
              Page: value
            })
          }}
          onChangeLimit={limit => {
            setSessionSearch({
              ...sessionSearch,
              PageSize: limit
            })
          }}
        />
      ) : null}
    </div>
  )
}

export default styled(SessionAnalysis)`
  overflow: hidden;
  padding-left: 12px;
  border-left: 1px solid ${variable.borderColor};
  .k-table {
    width: 100%;
  }
`
